'use client';

import { useEffect, useState } from 'react';
import { contactLookupPhoneKey, contactsApi } from '@/lib/api/contacts';
import type { Contact } from '@/types/contacts';

export type ContactLookupStatus = 'idle' | 'pending' | 'found' | 'not_found';

const LOOKUP_DEBOUNCE_MS = 220;

// Debounced CRM lookup by phone number. `status` distinguishes a completed
// lookup with no match ('not_found') from "nothing to look up" or a failed
// request ('idle'), so the UI only claims "Not found in CRM" when that is
// actually known. The lookup itself is session-cached for 10 minutes per
// normalized phone (see contactsApi.findContactByPhone), so repeat calls
// to/from the same number don't re-fetch.
export function useContactByPhone(phoneLike: string | null | undefined): {
  contact: Contact | null;
  status: ContactLookupStatus;
} {
  const normalized = contactLookupPhoneKey(String(phoneLike || ''));
  const lookupKey = normalized.replace(/\D/g, '').length >= 5 ? normalized : '';
  const [result, setResult] = useState<{ contact: Contact | null; status: ContactLookupStatus }>({
    contact: null,
    status: 'idle',
  });

  useEffect(() => {
    if (!lookupKey) {
      setResult({ contact: null, status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setResult({ contact: null, status: 'pending' });
    const timer = window.setTimeout(() => {
      void contactsApi.findContactByPhone(lookupKey).then((contact) => {
        if (!cancelled) {
          setResult({ contact, status: contact ? 'found' : 'not_found' });
        }
      }).catch(() => {
        if (!cancelled) {
          setResult({ contact: null, status: 'idle' });
        }
      });
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [lookupKey]);

  return result;
}
